import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Table } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSystemAlerts } from '../../../hooks/SystemAlertsProvider';
import { useStatusDashboardOverview } from '../../../hooks/useStatusDashboardOverview';
import {
  CONSUMOS_ROUTE,
  DASHBOARD_PANEL_ERROS_CONSECUTIVOS,
  ERROS_CONSECUTIVOS_ALERT_LABEL,
  LOW_BREAKER_BALANCE_ALERT_LABEL,
  NO_BREAKER_BALANCE_ALERT_LABEL,
  NO_PROXY_BALANCE_ALERT_LABEL,
} from '../../../constants/dashboardAlerts';
import { formatAlertTitle } from '../../../utils/alertTitle';
import { buildModalAbrangenciaColumns, buildOutdatedDivergenceColumns } from '../AbrangenciaColumns';
import { buildAbrangenciaKey, buildOutdatedDivergences } from '../../../utils/abrangenciaCompare';

type ExpandedPanel = 'outdated' | 'notDisplayed' | null;

interface SystemTopAlertsProps {
  isMobile?: boolean;
}

const alertContainerStyle = (isMobile: boolean): React.CSSProperties => ({
  padding: isMobile ? '8px 8px 8px' : '12px 16px 8px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const SystemTopAlerts: React.FC<SystemTopAlertsProps> = ({ isMobile = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: statusOverview } = useStatusDashboardOverview();
  const {
    compareResult,
    consecutiveErrorsTotal,
    consumosAlerts,
    showConsecutiveErrorsAlert,
    showLowBreakerBalanceAlert,
    showNoBreakerBalanceAlert,
    showNoProxyBalanceAlert,
    showNotDisplayedAlert,
    showOutdatedAlert,
    syncAlertsReady,
  } = useSystemAlerts();
  const [expandedPanel, setExpandedPanel] = useState<ExpandedPanel>(null);
  const [currentDate, setCurrentDate] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrentDate(new Date());
    }, 30_000);

    return () => window.clearInterval(timer);
  }, []);

  const currentMinute = currentDate.getMinutes();
  const errorPercentage = statusOverview?.statusSummary?.error.percentage ?? 0;
  const pendingPercentage = statusOverview?.statusSummary?.pending.percentage ?? 0;
  const errorAndPendingPercentage = errorPercentage + pendingPercentage;
  const showSystemAlert =
    Boolean(statusOverview) && currentMinute > 30 && errorAndPendingPercentage >= 30;

  const outdatedDivergences = useMemo(
    () => (compareResult ? buildOutdatedDivergences(compareResult) : []),
    [compareResult],
  );

  const outdatedColumns = useMemo(
    () => buildOutdatedDivergenceColumns(outdatedDivergences),
    [outdatedDivergences],
  );

  const notDisplayedColumns = useMemo(
    () => (compareResult ? buildModalAbrangenciaColumns(compareResult.notDisplayed) : []),
    [compareResult],
  );

  const togglePanel = (panel: Exclude<ExpandedPanel, null>) => {
    setExpandedPanel((current) => (current === panel ? null : panel));
  };

  const handleConsecutiveErrorsAlertClick = () => {
    if (location.pathname === '/dashboard/status') {
      window.dispatchEvent(new CustomEvent('dashboard:open-erros-consecutivos'));
      return;
    }

    navigate(`/dashboard/status?panel=${DASHBOARD_PANEL_ERROS_CONSECUTIVOS}`);
  };

  const handleConsumosAlertClick = () => {
    if (location.pathname !== CONSUMOS_ROUTE) {
      navigate(CONSUMOS_ROUTE);
    }
  };

  const lowBreakerBalanceCount = consumosAlerts?.lowBalanceBreakers.length ?? 0;
  const noBreakerBalanceCount = consumosAlerts?.noBalanceBreakers.length ?? 0;
  const noProxyBalanceCount = consumosAlerts?.noBalanceProxies.length ?? 0;

  const consecutiveAlertLabel = ERROS_CONSECUTIVOS_ALERT_LABEL;
  const hasAnyAlert =
    showSystemAlert ||
    showConsecutiveErrorsAlert ||
    showNoBreakerBalanceAlert ||
    showNoProxyBalanceAlert ||
    showLowBreakerBalanceAlert ||
    (syncAlertsReady && (showOutdatedAlert || showNotDisplayedAlert));

  if (!hasAnyAlert) {
    return null;
  }

  return (
    <div style={alertContainerStyle(isMobile)}>
      {showSystemAlert && (
        <>
          <style>
            {`
              @keyframes status-alert-blink {
                0%, 100% { opacity: 1; box-shadow: 0 0 0 rgba(255, 77, 79, 0); }
                50% { opacity: 0.55; box-shadow: 0 0 18px rgba(255, 77, 79, 0.45); }
              }
            `}
          </style>
          <Alert
            type="error"
            showIcon={false}
            closable={false}
            message={formatAlertTitle('Alta porcentagem de erros e warnings')}
            style={{
              marginBottom: 0,
              animation: 'status-alert-blink 1.1s infinite',
              border: '1px solid rgba(255, 77, 79, 0.35)',
            }}
          />
        </>
      )}

      {showConsecutiveErrorsAlert && (
        <Alert
          type="error"
          showIcon={false}
          closable={false}
          message={formatAlertTitle(
            `Abrangências com ${consecutiveAlertLabel}`,
            consecutiveErrorsTotal,
          )}
          style={{
            cursor: 'pointer',
            marginBottom: 0,
            border: '1px solid rgba(255, 77, 79, 0.35)',
          }}
          onClick={handleConsecutiveErrorsAlertClick}
        />
      )}

      {showNoBreakerBalanceAlert && (
        <Alert
          type="error"
          showIcon={false}
          closable={false}
          message={formatAlertTitle(NO_BREAKER_BALANCE_ALERT_LABEL, noBreakerBalanceCount)}
          style={{
            cursor: 'pointer',
            marginBottom: 0,
            border: '1px solid rgba(255, 77, 79, 0.35)',
          }}
          onClick={handleConsumosAlertClick}
        />
      )}

      {showNoProxyBalanceAlert && (
        <Alert
          type="error"
          showIcon={false}
          closable={false}
          message={formatAlertTitle(NO_PROXY_BALANCE_ALERT_LABEL, noProxyBalanceCount)}
          style={{
            cursor: 'pointer',
            marginBottom: 0,
            border: '1px solid rgba(255, 77, 79, 0.35)',
          }}
          onClick={handleConsumosAlertClick}
        />
      )}

      {showLowBreakerBalanceAlert && (
        <Alert
          type="warning"
          showIcon={false}
          closable={false}
          message={formatAlertTitle(LOW_BREAKER_BALANCE_ALERT_LABEL, lowBreakerBalanceCount)}
          style={{
            cursor: 'pointer',
            marginBottom: 0,
            border: '1px solid rgba(250, 173, 20, 0.35)',
          }}
          onClick={handleConsumosAlertClick}
        />
      )}

      {syncAlertsReady && showOutdatedAlert && compareResult && (
        <>
          <Alert
            type="warning"
            showIcon={false}
            message={formatAlertTitle(
              'Abrangência do status não está atualizada com base na produção',
              outdatedDivergences.length,
            )}
            style={{
              cursor: 'pointer',
              marginBottom: 0,
              ...(expandedPanel === 'outdated'
                ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: 'none' }
                : {}),
            }}
            onClick={() => togglePanel('outdated')}
          />
          {expandedPanel === 'outdated' && (
            <div
              style={{
                border: '1px solid #faad14',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                padding: 12,
                background: 'rgba(250, 173, 20, 0.04)',
              }}
            >
              <Table
                columns={outdatedColumns}
                dataSource={outdatedDivergences}
                rowKey={(row) => `${row.divergenceType}::${buildAbrangenciaKey(row)}`}
                pagination={{ pageSize: 10, showSizeChanger: true }}
                size="small"
                scroll={{ x: true }}
              />
            </div>
          )}
        </>
      )}

      {syncAlertsReady && showNotDisplayedAlert && compareResult && (
        <>
          <Alert
            type="warning"
            showIcon={false}
            message={formatAlertTitle(
              'Abrangência status não está sendo exibida',
              compareResult.notDisplayed.length,
            )}
            style={{
              cursor: 'pointer',
              marginBottom: 0,
              ...(expandedPanel === 'notDisplayed'
                ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: 'none' }
                : {}),
            }}
            onClick={() => togglePanel('notDisplayed')}
          />
          {expandedPanel === 'notDisplayed' && (
            <div
              style={{
                border: '1px solid #faad14',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                padding: 12,
                background: 'rgba(250, 173, 20, 0.04)',
              }}
            >
              <Table
                columns={notDisplayedColumns}
                dataSource={compareResult.notDisplayed}
                rowKey={(row) => buildAbrangenciaKey(row)}
                pagination={{ pageSize: 10, showSizeChanger: true }}
                size="small"
                scroll={{ x: true }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SystemTopAlerts;
